<template>
    <div>
      <CRUDCriteria :showReset="true" :showSearch="false">
        <template v-slot:criteriaRows>
            <v-row class="mr-6 ml-6 mb-n6">
                <v-col cols='2'>
                    <ClarionDateControl class="input-text-size"
                    @blurred="searchByCriteria(), changeTheCriteriaFromDate()"
                    v-model="criteriaState.fromDateValue"
                    label="From Date" :isToggle="false" v-if="enableDateControl"/>
                    </v-col>
                    <v-col cols='2'>
                    <ClarionDateControl class="input-text-size"
                    @blurred="searchByCriteria(), changeTheCriteriaToDate()"
                    v-model="criteriaState.toDateValue"
                    label="To Date" :isToggle="false" v-if="enableDateControl"/>
                    </v-col>
                <v-col cols="2" class="mt-4">
                  <BinaryToggle
                id="apportionmentTaxesRecordTypeToggle"
                aria-label="Record type: Normal or Trial"
                off-label="Normal"
                on-label="Trial"
                :value="criteriaState.typeOfRecord"
                @input="onChangeRecordType" />
                </v-col>
                <v-col cols="2" class="mt-4">
                <v-btn
                  :disabled="!isSuperAdmin"
                  class="ml-3"
                  color="primary"
                  small
                  dense
                  @click="deleteRecords"
                >
                  DELETE
                </v-btn>
                </v-col>
            </v-row>
            </template>
      </CRUDCriteria>
    </div>
  </template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex';
import BinaryToggle from '@/components/common/base/BinaryToggle.vue';
import ClarionDateControl from '@/components/common/ClarionDateControl.vue';
import { toIsoDate, getDefaultMonthRange } from '../../../util/shared/tmc-global';
import CRUDCriteria from '../../common/base/crud-2.0/CRUDCriteria.vue';
import { makeCriteriaStateNullIfEmptyString } from '../../tax/common-utils/CommonTaxFunctionalities';
import '../../../assets/style.css';
import { isAuthorised } from '../../../store/auth/token';
import { authRoles } from '../../../router/roleGroups';

export default {
  data() {
    // Use shared utility for default month range
    const { fromDateValue, toDateValue } = getDefaultMonthRange();
    return {
      isRecordTypeSwitch: false,
      enableDateControl: true,
      initialFromDateValue: fromDateValue,
      initialToDateValue: toDateValue,
    };
  },
  props: {
    baseCrudKey: {
      type: String,
      default: '',
    },
  },
  components: {
    BinaryToggle,
    ClarionDateControl,
    CRUDCriteria,
  },
  computed: {
    ...mapGetters('base/crud', ['criteria']),
    criteriaState: {
      get() {
        // If fromDateValue/toDateValue are not set, prepopulate them
        const state = this.criteria(this.baseCrudKey);
        if (!state.fromDateValue) {
          state.fromDateValue = this.initialFromDateValue;
        }
        if (!state.toDateValue) {
          state.toDateValue = this.initialToDateValue;
        }
        return state;
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    criteriaStateForMiscMtg: {
      get() {
        return this.criteria('miscMtgBaseCrudKey');
      },
      set(value) {
        this.setCriteria(['miscMtgBaseCrudKey', value]);
      },
    },
    criteriaStateForApportionment: {
      get() {
        return this.criteria('approtionmentTab');
      },
      set(value) {
        this.setCriteria(['approtionmentTab', value]);
      },
    },
    isSuperAdmin() {
      return isAuthorised(authRoles.SuperAdmin) || isAuthorised(authRoles.UserAdmins);
    },
  },
  watch: {
    'criteriaState.fromDate': {
      handler(newVal) {
        if (newVal || !newVal) {
          this.enableDateControl = false;
          this.criteriaState.fromDateValue = newVal;
          this.enableDateControl = true;
        }
      },
    },
    'criteriaState.toDate': {
      handler(newVal) {
        if (newVal || !newVal) {
          this.enableDateControl = false;
          this.criteriaState.toDateValue = newVal;
          this.enableDateControl = true;
        }
      },
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    ...mapActions(['reAuth', 'flashWarn']),
    ...mapActions(['reAuth', 'flashError']),
    ...mapActions('apportionment/apportionmentTab', [
      'deleteRecordFromApportionment',
    ]),
    // What v-model + @change did on the switch: write the flag the criteria carries, then let
    // changeRecordTypeSwitch reload against it.
    onChangeRecordType(val) {
      this.criteriaState.typeOfRecord = val;
      this.changeRecordTypeSwitch();
    },
    changeRecordTypeSwitch() {
      // this.criteriaState.typeOfRecord = !this.criteriaState.typeOfRecord;
      this.$emit('loadCRUDList');
    },
    callFromParentToUpdateDate() {
      const returnCriteria = makeCriteriaStateNullIfEmptyString(this.criteriaState);
      this.criteriaState = { ...returnCriteria };
    },
    searchByCriteria() {
      const returnCriteria = makeCriteriaStateNullIfEmptyString(this.criteriaState);
      this.criteriaState = { ...returnCriteria };
      this.$emit('loadCRUDList');
    },
    deleteRecords() {
      const { baseCrudKey } = this;
      const collectionName = 'taxes';
      if (this.isFiltersAvailable()) {
        if (window.confirm('Are you sure you want to delete these items?')) {
          const initialCriteria = {
            fromDate: this.criteriaState.fromDateValue
              ? toIsoDate(this.criteriaState.fromDateValue) : null,
            toDate: this.criteriaState.toDateValue
              ? toIsoDate(this.criteriaState.toDateValue) : null,
            typeOfRecord: this.criteriaState.typeOfRecord || false,
            collectionName,
          };
          this.deleteRecordFromApportionment({ baseCrudKey, criteria: initialCriteria });
          this.$emit('loadCRUDList');
        }
      } else {
        this.flashWarn('Please select date range!');
      }
    },
    isFiltersAvailable() {
      return (this.criteriaState.fromDateValue && this.criteriaState.toDateValue);
    },
    changeTheCriteriaFromDate() {
      this.criteriaStateForMiscMtg.fromDate = this.criteriaState.fromDateValue;
      this.criteriaStateForApportionment.fromDate = this.criteriaState.fromDateValue;
    },
    changeTheCriteriaToDate() {
      this.criteriaStateForMiscMtg.toDate = this.criteriaState.toDateValue;
      this.criteriaStateForApportionment.toDate = this.criteriaState.toDateValue;
    },
  },
};
</script>

<style scoped>
.align-right {
  display: flex;
  justify-content: end;
  /* margin-top: 1rem; */
}
</style>
